
'use client';

import { useState, useCallback } from 'react';
import * as bcrypt from 'bcryptjs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Copy, Check, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { bcryptGeneratorContent } from '../../data/bcrypt-generator-content';
import Link from 'next/link';

export default function BcryptGenerator() {
    const [password, setPassword] = useState('');
    const [hash, setHash] = useState('');
    const [saltRounds, setSaltRounds] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [verification, setVerification] = useState({
        password: '',
        hash: '',
        isVerified: false,
        isChecking: false,
    });
    const { toast } = useToast();

    const generateHash = useCallback(async () => {
        if (!password.trim()) {
            toast({
                title: 'Error',
                description: 'Please enter a password to hash',
                variant: 'destructive',
            });
            return;
        }

        try {
            setIsLoading(true);
            const salt = await bcrypt.genSalt(saltRounds);
            const hashed = await bcrypt.hash(password, salt);
            setHash(hashed);
            setIsCopied(false);
        } catch (error) {
            console.error('Error generating bcrypt hash:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate bcrypt hash',
                variant: 'destructive',
            });
            setHash('');
        } finally {
            setIsLoading(false);
        }
    }, [password, saltRounds, toast]);

    const verifyHash = useCallback(async () => {
        if (!verification.password || !verification.hash) {
            toast({
                title: 'Error',
                description: 'Please enter both password and hash for verification',
                variant: 'destructive',
            });
            return;
        }

        try {
            setVerification(prev => ({ ...prev, isChecking: true }));
            const isMatch = await bcrypt.compare(verification.password, verification.hash);
            setVerification(prev => ({
                ...prev,
                isVerified: isMatch,
                isChecking: false,
            }));

            toast({
                title: isMatch ? 'Success' : 'Error',
                description: isMatch ? 'Password matches the hash!' : 'Password does not match the hash',
                variant: isMatch ? 'default' : 'destructive',
            });
        } catch (error) {
            console.error('Error verifying bcrypt hash:', error);
            toast({
                title: 'Error',
                description: 'Failed to verify bcrypt hash. The hash format might be invalid.',
                variant: 'destructive',
            });
            setVerification(prev => ({
                ...prev,
                isVerified: false,
                isChecking: false,
            }));
        }
    }, [verification.password, verification.hash, toast]);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
        toast({
            title: 'Copied!',
            description: 'Hash has been copied to clipboard',
        });
    };

    return (

        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Bcrypt Hash Generator">
                Generate bcrypt hashes for passwords and verify hashes against plain text. Adjust salt rounds for security vs speed. Ideal for dev and testing of login systems. No sign-up.
            </ToolPageIntro>
            <div className="grid gap-6 md:grid-cols-2">
                {/* Hash Generator */}
                <Card>
                    <CardHeader>
                        <CardTitle>Generate Bcrypt Hash</CardTitle>
                        <CardDescription>
                            Securely hash your password using bcrypt
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter password to hash"
                                    className="pr-10"
                                />
                                <button
                                    type="button"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="saltRounds">Salt Rounds: {saltRounds}</Label>
                                <span className="text-sm text-gray-500">
                                    {saltRounds < 10 ? 'Faster (less secure)' : saltRounds > 12 ? 'Slower (more secure)' : 'Balanced'}
                                </span>
                            </div>
                            <Slider
                                id="saltRounds"
                                min={4}
                                max={20}
                                step={1}
                                value={[saltRounds]}
                                onValueChange={([value]) => setSaltRounds(value)}
                                className="py-4"
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Generated Hash</Label>
                                {hash && (
                                    <button
                                        type="button"
                                        onClick={() => copyToClipboard(hash)}
                                        className="text-sm text-primary hover:underline flex items-center gap-1"
                                    >
                                        {isCopied ? (
                                            <>
                                                <Check className="h-4 w-4" />
                                                <span>Copied!</span>
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="h-4 w-4" />
                                                <span>Copy</span>
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                            <div className="relative">
                                <Textarea
                                    readOnly
                                    value={hash}
                                    placeholder="Bcrypt hash will appear here"
                                    className="min-h-[100px] font-mono text-sm"
                                />
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter>
                        <Button
                            onClick={generateHash}
                            disabled={isLoading || !password.trim()}
                            className="w-full"
                        >
                            {isLoading ? (
                                <>
                                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                'Generate Hash'
                            )}
                        </Button>
                    </CardFooter>
                </Card>

                {/* Hash Verifier */}
                <Card>
                    <CardHeader>
                        <CardTitle>Verify Bcrypt Hash</CardTitle>
                        <CardDescription>
                            Check if a password matches a bcrypt hash
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="verify-password">Password</Label>
                            <Input
                                id="verify-password"
                                type="password"
                                value={verification.password}
                                onChange={(e) =>
                                    setVerification((prev) => ({
                                        ...prev,
                                        password: e.target.value,
                                        isVerified: false,
                                    }))
                                }
                                placeholder="Enter password to verify"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="verify-hash">Bcrypt Hash</Label>
                            <Textarea
                                id="verify-hash"
                                value={verification.hash}
                                onChange={(e) =>
                                    setVerification((prev) => ({
                                        ...prev,
                                        hash: e.target.value,
                                        isVerified: false,
                                    }))
                                }
                                placeholder="Enter bcrypt hash to verify against"
                                className="min-h-[100px] font-mono text-sm"
                            />
                        </div>

                        {verification.isVerified !== null && (
                            <div
                                className={cn(
                                    'p-3 rounded-md mt-2',
                                    verification.isVerified
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                )}
                            >
                                {verification.isVerified
                                    ? '✅ The password matches the hash!'
                                    : '❌ The password does not match the hash.'}
                            </div>
                        )}
                    </CardContent>
                    <CardFooter>
                        <Button
                            onClick={verifyHash}
                            disabled={
                                verification.isChecking ||
                                !verification.password.trim() ||
                                !verification.hash.trim()
                            }
                            className="w-full"
                        >
                            {verification.isChecking ? (
                                <>
                                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                    Verifying...
                                </>
                            ) : (
                                'Verify Hash'
                            )}
                        </Button>
                    </CardFooter>
                </Card>
            </div>

            <div className="mt-8">
                <Card>
                    <CardHeader>
                        <CardTitle>About Bcrypt</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                        <p>
                            <strong>Bcrypt</strong> is a password hashing function designed by Niels Provos and David Mazières, based on the Blowfish cipher. It is widely used for secure password storage.
                        </p>
                        <p>
                            <strong>Salt Rounds:</strong> The number of salt rounds determines the computational cost of hashing. Higher values are more secure but take longer to compute. A value of 10 is a good balance between security and performance for most applications.
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Note: All hashing is done in your browser. Your password is never sent to any server.
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={bcryptGeneratorContent.title}
                    description={bcryptGeneratorContent.description}
                    content={bcryptGeneratorContent.content}
                    faqs={bcryptGeneratorContent.faqs}
                    relatedTools={bcryptGeneratorContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
